import { createNanoIconSet } from "@-/com.npmjs.react-native-nano-icons";
import { Platform, processColor, type ColorValue } from "@-/com.npmjs.react-native";
import glyphMapFromJson from "#glyph-map" with { type: "json" };
import iconNamesFromJson from "#icon-names" with { type: "json" };

export const iconNames = iconNamesFromJson;
export type IconName = keyof typeof iconNames;
const glyphMap: {
  i: Record<string, (typeof glyphMapFromJson.i)[keyof typeof glyphMapFromJson.i]>;
  m: typeof glyphMapFromJson.m;
} = glyphMapFromJson;
// eslint-disable-next-line no-top/no-top-level-side-effects -- define the immutable icon font once at module load
export const NanoIcon = createNanoIconSet(glyphMap);
const translucentInkRegex = /^rgba\(0,0,0,([\d.]+)\)$/;

/** Nano's opacity flattener resolves translucent currentColor to black. Retint those
 * authored review-swipe layers while preserving their opacity and the white hand. */
export function getIconLayerColors(
  name: IconName,
  ink?: ColorValue,
): ColorValue | ColorValue[] | undefined {
  const entry = glyphMap.i[iconNames[name]];
  const layers = entry[1];
  if (!Array.isArray(layers)) {
    throw new TypeError("Invalid Nano Icons layers for " + name);
  }
  if (!layers.some(([, color]) => typeof color === "string" && color.startsWith("rgba(0,0,0,"))) {
    return ink;
  }
  const processedInk = ink === undefined ? undefined : processColor(ink);
  return layers.map(([, color]) => {
    if (typeof color !== "string") {
      throw new TypeError("Invalid Nano Icons color for " + name);
    }
    if (color === "currentColor") {
      return ink ?? "currentColor";
    }
    const alphaMatch = translucentInkRegex.exec(color);
    if (alphaMatch === null) {
      return color;
    }
    const alpha = Number(alphaMatch[1]);
    if (Platform.OS === "web") {
      return (
        "color-mix(in srgb, " +
        String(ink ?? "currentColor") +
        " " +
        alpha * 100 +
        "%, transparent)"
      );
    }
    if (typeof processedInk !== "number") {
      return ink ?? color;
    }
    const opacity = alpha * ((processedInk >>> 24) / 255);
    return (
      "rgba(" +
      ((processedInk >>> 16) & 255) +
      "," +
      ((processedInk >>> 8) & 255) +
      "," +
      (processedInk & 255) +
      "," +
      opacity +
      ")"
    );
  });
}
